import {
  EmailBulkSendAction,
  EmailBulkSendDraftState,
  type EmailBulkSendListFieldsFragment
} from '~/api/graphql';
import { type OutboxRow, compareOutboxRows } from './outbox-rows';

export type OutboxBulkSend = EmailBulkSendListFieldsFragment;

/** The agency's recent batches, by ID. */
export type OutboxBatches = ReadonlyMap<string, OutboxBulkSend>;

export const NO_OUTBOX_BATCHES: OutboxBatches = new Map();

export const outboxBatches = (bulkSends: OutboxBulkSend[]): OutboxBatches =>
  new Map(
    bulkSends.filter((bulkSend) => !!bulkSend.sentAt).map((batch) => [batch.id, batch] as const)
  );

/** Left a draft in Cara by its batch's Send, and not sent or saved to a mailbox since. */
export const isOutboxRowHeldBack = (row: OutboxRow): boolean =>
  !row.outbound && !row.draft && !!row.artifact?.heldBackFromBulkSendId;

/** Waiting its turn in a Send all: the send it's waiting on is the batch's own. */
export const isOutboxRowQueuedInBatch = (row: OutboxRow, batches: OutboxBatches): boolean =>
  batches.get(row.outbound?.bulkSendId ?? '')?.action === EmailBulkSendAction.SendAll;

/** Whether Cara is still writing any of the bulk email's emails. */
export const isBulkSendDrafting = (bulkSend: {
  recipients: { draftState: EmailBulkSendDraftState }[];
}): boolean =>
  bulkSend.recipients.some(
    (recipient) => recipient.draftState === EmailBulkSendDraftState.Drafting
  );

/**
 * Outgoing's rows with each bulk email as one row, placed by when it was sent, or made while
 * it's a bulk draft. Its filters can't be asked about a bulk email itself, so a filtered list
 * keeps only the batch it's filtered to. A bulk draft both reads return is listed once.
 */
export const outboxRowsWithBulkSends = (
  rows: OutboxRow[],
  bulkSends: OutboxBulkSend[],
  opts: { isFiltered: boolean; filteredBatch?: OutboxBulkSend | null }
): OutboxRow[] => {
  const shownBulkSends = opts.isFiltered
    ? opts.filteredBatch
      ? [opts.filteredBatch]
      : []
    : bulkSends;
  const bulkSendsById = new Map(shownBulkSends.map((bulkSend) => [bulkSend.id, bulkSend]));
  const bulkSendRows = [...bulkSendsById.values()].map(
    (bulkSend): OutboxRow => ({
      key: `bulkSend:${bulkSend.id}`,
      sortAt: new Date(bulkSend.sentAt ?? bulkSend.createdAt),
      earlierSends: [],
      bulkSend
    })
  );
  return [...rows, ...bulkSendRows].sort(compareOutboxRows);
};

/**
 * The bulk email a row's email belongs to: the batch its send went out in, the batch that held
 * it back, or else the bulk email whose recipients list it, as for a draft Move all to drafts
 * saved, which says neither.
 */
export const outboxRowBulkSendId = (
  row: OutboxRow,
  bulkSendIdByEmailId: ReadonlyMap<string, string>
): string | undefined => {
  const emailId =
    row.artifact?.id ?? row.draft?.workspaceArtifactId ?? row.outbound?.workspaceArtifactId;
  return (
    row.outbound?.bulkSendId ||
    row.earlierSends.find((send) => send.bulkSendId)?.bulkSendId ||
    row.artifact?.heldBackFromBulkSendId ||
    (emailId ? bulkSendIdByEmailId.get(emailId) : undefined)
  );
};

/**
 * Outgoing's rows, read before the watermark, with each bulk email's own emails under its row,
 * newest first. A group sits by its latest activity, its row's or its newest email's, so a batch
 * whose emails went out after it was sent leads with its row. An email whose bulk email has no
 * row in the list keeps its own place. Only rows above the watermark are kept, a bulk row also
 * when one of its emails is.
 */
export const groupOutboxRowsUnderBulkSends = (
  rows: OutboxRow[],
  watermarkMs: number
): OutboxRow[] => {
  const bulkSendIdByEmailId = new Map<string, string>();
  for (const { bulkSend } of rows) {
    for (const recipient of bulkSend?.recipients ?? []) {
      if (bulkSend && recipient.artifactId) {
        bulkSendIdByEmailId.set(recipient.artifactId, bulkSend.id);
      }
    }
  }
  const listedBulkSendIds = new Set(
    rows.flatMap(({ bulkSend }) => (bulkSend ? [bulkSend.id] : []))
  );
  const isAboveWatermark = (row: OutboxRow) => row.sortAt.getTime() > watermarkMs;
  const emailsByBulkSendId = new Map<string, OutboxRow[]>();
  const placedRows: OutboxRow[] = [];
  for (const row of rows) {
    const bulkSendId = row.bulkSend ? undefined : outboxRowBulkSendId(row, bulkSendIdByEmailId);
    if (bulkSendId && listedBulkSendIds.has(bulkSendId)) {
      if (isAboveWatermark(row)) {
        emailsByBulkSendId.set(bulkSendId, [...(emailsByBulkSendId.get(bulkSendId) ?? []), row]);
      }
    } else {
      placedRows.push(row);
    }
  }
  const groups = placedRows.flatMap((row) => {
    const emails = row.bulkSend ? (emailsByBulkSendId.get(row.bulkSend.id) ?? []) : [];
    const groupRows = [row, ...emails].sort(compareOutboxRows);
    const latestActivity = groupRows[0];
    return isAboveWatermark(latestActivity) ? [{ latestActivity, rows: [row, ...emails] }] : [];
  });
  return groups
    .sort((a, b) => compareOutboxRows(a.latestActivity, b.latestActivity))
    .flatMap((group) => group.rows);
};
